<template>
  <perfect-scrollbar class="min-w-0">
    <div class="flex flex-col gap-4">
      <template v-for="group in groupedIndexes">
        <!-- Legacy index -->
        <CollapsibleList v-if="!group.hasIndexGroup"
                         :key="`legacy-${group.id}`"
                         class="legacy-index-collapsible"
                         :items="[group]"
                         :value="getLegacyOpenState(group.id)"
                         @input="setLegacyOpenState(group.id, $event)"
                         :selected-index="0"
                         :is-selected="isLegacyIndexHeaderSelected(group)"
        >
          <template #title="{ item: legacyGroup }">
            <div class="flex justify-between items-center w-full cursor-pointer min-w-0 flex-1"
                 @click="handleLegacyIndexSelect(legacyGroup)"
            >
              <input-with-error-message v-if="isStandaloneIndexRenaming(legacyGroup)"
                                        id="indexes-list-name-input"
                                        :value.sync="indexName"
                                        :is-loading="isIndexNameLoading"
                                        @escape="setRenameIndex()"
                                        @submit="renameIndex(legacyGroup.primaryIndex)"
                                        class="w-full py-4 pl-2"
              />
              <div v-else class="flex gap-3">
                <span class="font-semibold text-black truncate">{{ legacyGroup.primaryIndex.name }}</span>
                <index-execution-next-update v-if="legacyGroup.primaryIndex.schedule_params"
                                             :cron-expression="getCronExpression(legacyGroup.primaryIndex.schedule_params)"
                                             :icon-class="'w-4'"
                />
                <index-execution-status-icon :index="legacyGroup.primaryIndex"
                                             :index-position="getIndexPosition(legacyGroup.primaryIndex)"
                />
                <index-execution-last-update v-if="hasLastFinishedExecution(legacyGroup.primaryIndex)"
                                              :index-execution="legacyGroup.primaryIndex.last_finished_index_execution"
                                              class="font-normal"
                />
                <index-execution-warning v-else-if="!hasLastIndexExecution(legacyGroup.primaryIndex)"
                                                :has-schedule-params="!!legacyGroup.primaryIndex.schedule_params"
                                                class="font-normal"/>
              </div>

              <div class="w-12 flex-shrink-0 flex items-center justify-end pr-2">
                <side-list-menu v-if="!isStandaloneIndexRenaming(legacyGroup)"
                                :items="getGroupMenuItems(legacyGroup)"
                                @rename="setRenameIndex(legacyGroup.primaryIndex)"
                                @reindex-all-sources="$emit('start-reindex', legacyGroup.primaryIndex)"
                                @delete="$emit('request-delete', legacyGroup.primaryIndex)"
                />
              </div>
            </div>
          </template>

          <template #content="{ item: legacyGroup }">
            <div class="flex justify-between items-center py-3 pr-7">
              <span class="text-gray pl-10">{{ getSourcesSummaryText(legacyGroup.primaryIndex) }}</span>
              <settings-indexes-add-source-button :is-adding="isAddingNewIndexSource && isLegacyIndexSelected(legacyGroup)"
                                                  @add-source="handleAddIndexSourceClick($event, legacyGroup.primaryIndex)"
              />
            </div>

            <settings-indexes-list-source v-for="(idxSource, j) in legacyGroup.primaryIndex.sources"
                                          :key="idxSource.id || j"
                                          :index-source="idxSource"
                                          :index="legacyGroup.primaryIndex"
                                          :is-selected="isSelectedIndexSource(legacyGroup.primaryIndex, j)"
                                          :is-hovered="isSourceHovered(legacyGroup.primaryIndex, j)"
                                          :is-renaming="isSourceRenaming(legacyGroup.primaryIndex, j)"
                                          :rename-value.sync="indexSourceName"
                                          :is-rename-loading="isIndexSourceNameLoading"
                                          @mouseenter="setSourceHover(legacyGroup.primaryIndex, j)"
                                          @mouseleave="clearSourceHover"
                                          @select="handleIndexSourceClick(legacyGroup.primaryIndex, j)"
                                          @rename="setRenameIndexSource(legacyGroup.primaryIndex, j, idxSource.name)"
                                          @cancel-rename="setRenameIndexSource()"
                                          @submit-rename="renameIndexSourceName(legacyGroup.primaryIndex)"
                                          @reindex="$emit('start-reindex', legacyGroup.primaryIndex)"
                                          @delete="$emit('delete-index-source', idxSource)"
            />
          </template>
        </CollapsibleList>

        <!-- Index group -->
        <CollapsibleList v-else
                         :key="`group-${group.id}`"
                         class="index-group-collapsible"
                         :items="[group]"
                         :value="getGroupOpenState(group.id)"
                         @input="setGroupOpenState(group.id, $event)"
                         :selected-index="0"
                         :is-selected="isIndexGroupHeaderSelected(group)"
        >
          <template #title="{ item: indexGroup }">
            <div class="flex justify-between items-center cursor-pointer min-w-0 flex-1"
                 @click="handleGroupSelect(indexGroup)"
            >
              <input-with-error-message v-if="isGroupRenaming(indexGroup)"
                                        id="indexes-list-group-name-input"
                                        :value.sync="indexName"
                                        :is-loading="isIndexNameLoading"
                                        @escape="setRenameIndex()"
                                        @submit="renameIndexGroup(indexGroup)"
                                        class="py-4 pl-2"
              />
              <div v-else class="flex items-center gap-3 min-w-0">
                <span class="inline-flex items-center gap-2 min-w-0 font-semibold text-black">
                  <span class="truncate">{{ getGroupTitle(indexGroup) }}</span>
                  <vx-tooltip :text="badgeTooltip(indexGroup)" class="inline-flex items-center flex-shrink-0">
                    <span class="count-badge"
                          :class="{ 'count-badge-warning': hasMissingKnowledgeMarkets(indexGroup) }"
                    >{{ getGroupBadgeCount(indexGroup) }}</span>
                  </vx-tooltip>
                </span>
                <index-group-execution-status-icon :indexes="indexGroup.indexes"/>
              </div>

              <div class="flex items-center gap-1 flex-shrink-0">
                <button type="button"
                        class="bg-transparent border-0 cursor-pointer p-3 rounded-lg hover:bg-primary-light flex items-center gap-1"
                        :class="activeAddMarketGroupId === indexGroup.id ? 'text-focus-error' : 'text-gray-dark'"
                        @click.stop="$emit('request-add-market', indexGroup)"
                >
                  <template v-if="activeAddMarketGroupId === indexGroup.id">
                    <span>Cancel</span>
                    <CloseIcon class="h-4 w-4"/>
                  </template>
                  <template v-else>
                    + Add market
                  </template>
                </button>
                <div @click.prevent.stop class="w-12 flex-shrink-0 flex items-center justify-end pr-2">
                  <side-list-menu v-if="!isGroupRenaming(indexGroup)"
                                  :items="getGroupMenuItems(indexGroup)"
                                  @rename="setRenameIndexGroup(indexGroup)"
                                  @reindex-all-markets="$emit('start-reindex-all-markets', indexGroup)"
                                  @delete="$emit('request-delete-index-group', indexGroup)"
                  />
                </div>
              </div>
            </div>
          </template>

          <template #content="{ item: indexGroup }">
            <CollapsibleList v-if="indexGroup.markets.length > 0"
                             class="nested-collapsible-list gap-0"
                             :items="indexGroup.indexes"
                             :value="openMarkets[indexGroup.id] || {}"
                             @input="setMarketOpenState(indexGroup.id, $event)"
                             :selected-index="selectedMarketIndex(indexGroup)"
                             :is-selected="['market', 'addSource'].includes(selectedPanelLevel) && selectedMarketIndex(indexGroup) >= 0 && selectedIndexSourcePosition === -1"
            >
              <template #title="{ item: index }">
                <div class="flex flex-1 min-w-0 justify-between items-center cursor-pointer"
                     :class="{ 'bg-primary-light': isMarketSelected(index) }"
                     @mouseenter="hoveredMarketIndexId = index.id"
                     @mouseleave="hoveredMarketIndexId = null"
                     @focusin="focusedMarketIndexId = index.id"
                     @focusout="focusedMarketIndexId = null"
                     @click="selectIndex(index)"
                >
                  <input-with-error-message v-if="isIndexRenaming(index)"
                                            id="indexes-list-name-input"
                                            :value.sync="indexName"
                                            :is-loading="isIndexNameLoading"
                                            @escape="setRenameIndex()"
                                            @submit="renameIndex(index)"
                                            class="w-full py-3 pl-2"
                  />
                  <div v-else class="flex items-center gap-3 min-w-0 flex-1">
                    <span class="font-semibold text-black min-w-0 truncate">{{ index.name }}</span>
                    <index-execution-next-update v-if="index.schedule_params"
                                               :cron-expression="getCronExpression(index.schedule_params)"
                                               :icon-class="'w-4'"
                    />
                    <index-execution-status-icon :index="index" :index-position="getIndexPosition(index)"/>
                    <index-execution-last-update v-if="hasLastFinishedExecution(index)" :index-execution="index.last_finished_index_execution"/>
                    <index-execution-warning v-else-if="!hasLastIndexExecution(index)" :has-schedule-params="!!index.schedule_params"/>
                  </div>

                  <div class="flex items-center gap-1 flex-shrink-0">
                    <settings-indexes-add-source-button :is-adding="isAddingNewIndexSource && isMarketSelected(index)"
                                                        :style="showAddSourceButton(index) ? null : { visibility: 'hidden', pointerEvents: 'none' }"
                                                        @add-source="handleAddIndexSourceClick($event, index, true)"
                    />
                    <div @click.prevent.stop
                         class="w-12 flex-shrink-0 flex items-center justify-end"
                    >
                      <div :class="{ 'invisible pointer-events-none': !showMarketMenu(index) }"
                           class="flex items-center justify-end pr-2"
                      >
                        <side-list-menu v-if="!isIndexRenaming(index)"
                                        :items="getIndexMenuItems()"
                                        @rename="setRenameIndex(index)"
                                        @reindex-all-sources="$emit('start-reindex', index)"
                                        @delete="$emit('request-delete', index)"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <template #content="{ item: index }">
                <settings-indexes-list-source v-for="(idxSource, j) in index.sources"
                                              :key="idxSource.id || j"
                                              :index-source="idxSource"
                                              :index="index"
                                              :is-selected="isSelectedIndexSource(index, j)"
                                              :is-hovered="isSourceHovered(index, j)"
                                              :is-renaming="isSourceRenaming(index, j)"
                                              :rename-value.sync="indexSourceName"
                                              :is-rename-loading="isIndexSourceNameLoading"
                                              class="pl-9"
                                              @mouseenter="setSourceHover(index, j)"
                                              @mouseleave="clearSourceHover"
                                              @select="handleIndexSourceClick(index, j)"
                                              @rename="setRenameIndexSource(index, j, idxSource.name)"
                                              @cancel-rename="setRenameIndexSource()"
                                              @submit-rename="renameIndexSourceName(index)"
                                              @reindex="$emit('start-reindex', index)"
                                              @delete="$emit('delete-index-source', idxSource)"
                />
              </template>
            </CollapsibleList>
          </template>
        </CollapsibleList>
      </template>
    </div>
  </perfect-scrollbar>
</template>

<script>
import { PerfectScrollbar } from 'vue2-perfect-scrollbar'
import CollapsibleList from '@/components/common/CollapsibleList.vue'
import InputWithErrorMessage from '@/components/common/InputWithErrorMessage.vue'
import SideListMenu from '@/components/common/SideListMenu'
import IndexExecutionStatusIcon from '@/components/settings/indexes/IndexExecutionStatusIcon.vue'
import IndexGroupExecutionStatusIcon from '@/components/settings/indexes/IndexGroupExecutionStatusIcon.vue'
import IndexExecutionLastUpdate from '@/components/settings/indexes/IndexExecutionLastUpdate.vue'
import IndexExecutionNextUpdate from '@/components/settings/indexes/IndexExecutionNextUpdate.vue'
import IndexExecutionWarning from '@/components/settings/indexes/IndexExecutionWarning.vue'
import SettingsIndexesListSource from '@/components/settings/indexes/SettingsIndexesListSource.vue'
import SettingsIndexesAddSourceButton from '@/components/settings/indexes/SettingsIndexesAddSourceButton.vue'
import CloseIcon from '@/assets/images/icons/CloseIcon.vue'
import marketModule from '@/store/modules/market.module'
import { ROUTE_NAMES } from '@/router'
import { MENU_ITEMS } from '@/utils/editor.constants'
import { groupIndexes, buildMissingKnowledgeMarketsTooltip, getMissingKnowledgeMarketsForIndexGroup } from '@/utils/index.utils'

export default {
  name: 'SettingsIndexesList',

  components: {
    CloseIcon,
    SettingsIndexesAddSourceButton,
    SettingsIndexesListSource,
    PerfectScrollbar,
    CollapsibleList,
    SideListMenu,
    IndexExecutionStatusIcon,
    IndexGroupExecutionStatusIcon,
    IndexExecutionLastUpdate,
    IndexExecutionNextUpdate,
    IndexExecutionWarning,
    InputWithErrorMessage
  },

  props: {
    selectedIndexPosition: {
      type: Number,
      default: 0
    },
    selectedIndexSourcePosition: {
      type: Number,
      default: -1
    },
    selectedPanelLevel: {
      type: String,
      default: 'group'
    },
    indexes: {
      type: Array,
      required: true
    },
    indexGroupsById: {
      type: Object,
      default: () => ({})
    },
    activeAddMarketGroupId: {
      type: String,
      default: null
    },
    isIndexNameLoading: {
      type: Boolean,
      default: false
    },
    isIndexSourceNameLoading: {
      type: Boolean,
      default: false
    },
    isAddingNewIndexSource: {
      type: Boolean,
      default: false
    }
  },

  emits: [
    'rename',
    'rename-index-group',
    'rename-index-source',
    'update-index-source-position',
    'delete-index-source',
    'request-delete',
    'request-delete-index-group',
    'show-add-index-source',
    'start-reindex',
    'start-reindex-all-markets',
    'request-add-market',
    'select-index-index',
    'select-market-index'
  ],

  watch: {
    isIndexNameLoading(isIndexNameLoading) {
      if (!isIndexNameLoading) {
        this.setRenameIndex()
        this.renameIndexGroupId = null
      }
    },
    isIndexSourceNameLoading(isIndexSourceNameLoading) {
      if (!isIndexSourceNameLoading) {
        this.setRenameIndexSource()
      }
    },
    selectedIndexPosition: {
      immediate: true,
      handler(newIndex) {
        if (newIndex < 0) return

        const selectedIndex = this.indexes[newIndex]
        const selectedGroup = this.groupedIndexes.find(group => {
          if (group.primaryIndex?.id === selectedIndex?.id) return true
          return group.markets.some(market => market.index?.id === selectedIndex?.id)
        })
        if (!selectedGroup) return

        if (selectedGroup.hasIndexGroup) {
          this.$set(this.openGroups, selectedGroup.id, true)
          if (!selectedGroup.markets.length) return

          const marketOpenItems = this.openMarkets[selectedGroup.id] || {}
          const selectedMarketIndex = selectedGroup.indexes.findIndex(item => item.id === selectedIndex.id)
          if (selectedMarketIndex < 0) return

          this.$set(marketOpenItems, selectedMarketIndex, true)
          this.$set(this.openMarkets, selectedGroup.id, marketOpenItems)
        } else {
          this.$set(this.openLegacyGroups, selectedGroup.id, true)
        }
      }
    }
  },

  data() {
    return {
      openGroups: {},
      openLegacyGroups: {},
      openMarkets: {},
      hoveredMarketIndexId: null,
      focusedMarketIndexId: null,
      hoveredSourceKey: null,
      renameIndexPosition: -1,
      renameIndexGroupId: null,
      renameIndexSourceKey: null,
      indexName: '',
      indexSourceName: ''
    }
  },

  computed: {
    markets: marketModule.getMarketList,

    selectedIndex() {
      return this.indexes[this.selectedIndexPosition]
    },
    groupedIndexes() {
      return groupIndexes(this.indexes, this.indexGroupsById)
    },
    selectedGroupIndex() {
      if (this.selectedIndexPosition < 0) return -1

      const selectedIndex = this.indexes[this.selectedIndexPosition]
      if (!selectedIndex) return -1

      return this.groupedIndexes.findIndex(group => {
        if (group.primaryIndex?.id === selectedIndex?.id) return true
        return group.markets.some(market => market.index?.id === selectedIndex?.id)
      })
    }
  },
 
  methods: {
    getIndexPosition(index) {
      return this.indexes.findIndex(item => item.id === index.id)
    },
    getGroupTitle(group) {
      return group.indexGroup?.name || this.indexGroupsById[group.id]?.name || group.name
    },
    getGroupMenuItems(group) {
      if (group.hasIndexGroup) {
        return [MENU_ITEMS.rename, MENU_ITEMS.reindexAllMarkets, MENU_ITEMS.delete]
      }
      return [MENU_ITEMS.rename, MENU_ITEMS.reindexAllSources, MENU_ITEMS.delete]
    },
    getGroupOpenState(groupId) {
      return { 0: !!this.openGroups[groupId] }
    },
    setGroupOpenState(groupId, openState) {
      this.$set(this.openGroups, groupId, !!openState[0])
    },
    getLegacyOpenState(groupId) {
      return { 0: !!this.openLegacyGroups[groupId] }
    },
    setLegacyOpenState(groupId, openState) {
      this.$set(this.openLegacyGroups, groupId, !!openState[0])
    },
    isIndexGroupHeaderSelected(group) {
      const groupIndex = this.groupedIndexes.findIndex(item => item.id === group.id)
      return ['group', 'addMarket'].includes(this.selectedPanelLevel) &&
        this.selectedGroupIndex === groupIndex &&
        this.renameIndexPosition === -1 &&
        !this.isGroupRenaming(group)
    },
    isLegacyIndexSelected(group) {
      return this.getIndexPosition(group.primaryIndex) === this.selectedIndexPosition
    },
    isLegacyIndexHeaderSelected(group) {
      return this.isLegacyIndexSelected(group) &&
        this.selectedIndexSourcePosition === -1 &&
        this.renameIndexPosition === -1 &&
        !this.isStandaloneIndexRenaming(group)
    },
    handleLegacyIndexSelect(group) {
      const indexPosition = this.getIndexPosition(group.primaryIndex)
      if (indexPosition < 0) return

      if (indexPosition === this.selectedIndexPosition) {
        this.updateIndexSourcePosition(-1)
      } else {
        this.$emit('select-index-index', indexPosition)
      }

      this.$set(this.openLegacyGroups, group.id, true)
      this.$router.push(this.getIndexRoute(group.primaryIndex.id))
    },
    getIndexMenuItems() {
      const menuItemReindex = { ...MENU_ITEMS.reindexAllSources, label: 'Reindex' }
      return [MENU_ITEMS.rename, menuItemReindex, MENU_ITEMS.delete]
    },
    getGroupMarketIds(group) {
      return new Set(
        (group.markets || [])
          .map(market => market.marketId)
          .filter(Boolean)
      )
    },
    getMissingKnowledgeMarkets(group) {
      return getMissingKnowledgeMarketsForIndexGroup(this.indexes, group.id, this.markets)
    },
    hasMissingKnowledgeMarkets(group) {
      return group.hasIndexGroup ? this.getMissingKnowledgeMarkets(group).length > 0 : false
    },
    getGroupBadgeCount(group) {
      if (!group.hasIndexGroup) {
        return group.badgeCount || 0
      }

      const totalMarkets = this.markets.length
      if (this.hasMissingKnowledgeMarkets(group)) {
        const coveredCount = this.getGroupMarketIds(group).size
        return `⚠️ ${coveredCount}/${totalMarkets}`
      }

      return totalMarkets
    },
    badgeTooltip(group) {
      if (!group.hasIndexGroup) {
        const count = group.badgeCount || 0
        return `${count} ${count === 1 ? 'source' : 'sources'}`
      }

      const missingMarkets = this.getMissingKnowledgeMarkets(group)
      if (missingMarkets.length === 0) {
        const count = this.markets.length
        return `${count} ${count === 1 ? 'market' : 'markets'}`
      }

      return buildMissingKnowledgeMarketsTooltip(missingMarkets)
    },
    setMarketOpenState(groupId, openState) {
      this.$set(this.openMarkets, groupId, openState)
    },
    selectedMarketIndex(group) {
      if (!['market', 'addSource', 'source'].includes(this.selectedPanelLevel)) return -1
      if (!this.selectedIndex) return -1
      return group.indexes.findIndex(item => item.id === this.selectedIndex.id)
    },
    isMarketSelected(index) {
      return ['market', 'addSource'].includes(this.selectedPanelLevel) &&
        this.selectedIndex?.id === index.id &&
        this.selectedIndexSourcePosition === -1
    },
    showMarketMenu(index) {
      return this.isMarketSelected(index) || this.hoveredMarketIndexId === index.id
    },
    showAddSourceButton(index) {
      return this.isMarketSelected(index) ||
        this.hoveredMarketIndexId === index.id ||
        this.focusedMarketIndexId === index.id
    },
    getSourcesSummaryText(index) {
      const sourcesLength = index.sources?.length || 0
      return `${sourcesLength} ${sourcesLength === 1 ? 'source' : 'sources'}`
    },
    getCronExpression(scheduleParams) {
      return scheduleParams?.cronExpression || null
    },
    handleAddIndexSourceClick({ el, sourceType }, index = null, isMarket = false) {
      // A source type is only provided when picking an option from the "+ Add
      // source" dropdown; the Cancel button emits without one
      const isAdding = !!sourceType
      if (isAdding && index) {
        this.selectIndexForAddSource(index, isMarket)
      }
      this.$emit('show-add-index-source', { isAdding, sourceType })
      el.blur()
    },
    selectIndexForAddSource(index, isMarket) {
      const indexPosition = this.getIndexPosition(index)
      if (indexPosition < 0) return

      this.$emit(isMarket ? 'select-market-index' : 'select-index-index', indexPosition)
      if (this.$route.params.knowledgeBaseId !== index.id) {
        this.$router.push(this.getIndexRoute(index.id))
      }
    },
    handleIndexSourceClick(index, indexSourcePosition) {
      const indexPosition = this.getIndexPosition(index)
      if (indexPosition < 0) return

      const isSameIndex = this.selectedIndex?.id === index.id
      const isTogglingOff = isSameIndex &&
        this.selectedIndexSourcePosition !== -1 &&
        indexSourcePosition === this.selectedIndexSourcePosition
      const newSourcePosition = isTogglingOff ? -1 : indexSourcePosition

      if (!isSameIndex) {
        const isMarket = !!index.index_group_id
        this.$emit(isMarket ? 'select-market-index' : 'select-index-index', indexPosition, newSourcePosition)
        if (this.$route.params.knowledgeBaseId !== index.id) {
          this.$router.push(this.getIndexRoute(index.id))
        }
        return
      }

      this.updateIndexSourcePosition(newSourcePosition)
    },
    hasLastIndexExecution(index) {
      return !!index.last_index_execution?.created_on
    },
    hasLastFinishedExecution(index) {
      return !!index.last_finished_index_execution?.finished_on
    },
    updateIndexSourcePosition(indexSourcePosition) {
      this.$emit('update-index-source-position', indexSourcePosition)
    },
    isGroupRenaming(group) {
      return group.hasIndexGroup && this.renameIndexGroupId === group.id
    },
    isStandaloneIndexRenaming(group) {
      return !group.hasIndexGroup && this.renameIndexPosition === this.getIndexPosition(group.primaryIndex)
    },
    isIndexRenaming(index) {
      return this.renameIndexPosition === this.getIndexPosition(index)
    },
    setRenameIndexGroup(group) {
      this.renameIndexGroupId = group.id
      this.renameIndexPosition = -1
      this.indexName = this.getGroupTitle(group)
    },
    setRenameIndex(index = null) {
      if (index) {
        this.renameIndexPosition = this.getIndexPosition(index)
        this.renameIndexGroupId = null
        this.indexName = index.name
        return
      }
      this.renameIndexPosition = -1
      this.renameIndexGroupId = null
      this.indexName = ''
    },
    setRenameIndexSource(index = null, indexSourcePosition = -1, indexSourceName = '') {
      if (index === null) {
        this.renameIndexSourceKey = null
        this.indexSourceName = ''
        return
      }
      this.renameIndexSourceKey = `${index.id}:${indexSourcePosition}`
      this.indexSourceName = indexSourceName
    },
    isSourceRenaming(index, sourcePosition) {
      return this.renameIndexSourceKey === `${index.id}:${sourcePosition}`
    },
    isSourceHovered(index, sourcePosition) {
      return this.hoveredSourceKey === `${index.id}:${sourcePosition}`
    },
    setSourceHover(index, sourcePosition) {
      this.hoveredSourceKey = `${index.id}:${sourcePosition}`
    },
    clearSourceHover() {
      this.hoveredSourceKey = null
    },
    isSelectedIndexSource(index, sourcePosition) {
      return this.selectedIndex?.id === index.id && sourcePosition === this.selectedIndexSourcePosition
    },
    renameIndexGroup(group) {
      if (!this.renameIndexGroupId) return
      const currentName = this.getGroupTitle(group)
      if (currentName === this.indexName) {
        this.setRenameIndex()
        return
      }
      this.$emit('rename-index-group', { id: group.id, name: this.indexName })
    },
    renameIndex(index) {
      if (this.renameIndexPosition === -1) return
      if (index.name === this.indexName) {
        this.setRenameIndex()
        return
      }
      this.$emit('rename', { ...index, name: this.indexName }, this.renameIndexPosition)
    },
    renameIndexSourceName(index) {
      if (!this.renameIndexSourceKey) return

      const [, sourcePosition] = this.renameIndexSourceKey.split(':')
      const indexSource = index.sources[Number(sourcePosition)]
      if (indexSource.name === this.indexSourceName) {
        this.setRenameIndexSource()
        return
      }
      this.$emit('rename-index-source', { ...indexSource, name: this.indexSourceName })
    },
    selectIndex(index) {
      const indexPosition = this.getIndexPosition(index)
      if (indexPosition < 0) return
      this.$emit('select-market-index', indexPosition)
      this.$router.push(this.getIndexRoute(index.id))
    },
    handleGroupSelect(group) {
      this.$set(this.openGroups, group.id, true)

      const targetIndex = group.primaryIndex || group.indexes?.[0]
      if (!targetIndex) return

      const indexPosition = this.getIndexPosition(targetIndex)
      if (indexPosition < 0) return

      this.$emit('select-index-index', indexPosition)
      this.$router.push(this.getIndexRoute(targetIndex.id))
    },
    getIndexRoute(indexId) {
      return { name: ROUTE_NAMES.knowledgeBases, params: { knowledgeBaseId: indexId } }
    }
  }
}
</script>

<style scoped lang="scss">
.count-badge {
  @apply h-5 min-w-5 px-1 rounded-full inline-flex items-center justify-center bg-white border border-solid border-gray-border text-xs text-gray-dark;
  line-height: 1;
}

.count-badge-warning {
  min-width: auto;
  @apply px-1.5;
}

.index-group-collapsible ::v-deep .collapsible-list,
.legacy-index-collapsible ::v-deep .collapsible-list {
  @apply gap-0;
}

.legacy-index-collapsible ::v-deep .collapsible-list-item-content {
  border-top: 0 !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.legacy-index-collapsible ::v-deep .collapsible-list-item-content.is-expanded,
.index-group-collapsible ::v-deep .collapsible-list-item-content.is-expanded {
  overflow-x: hidden;
}

.nested-collapsible-list ::v-deep .collapsible-list-item {
  @apply border-0 border-b border-solid border-soft rounded-none;
}

.nested-collapsible-list ::v-deep .collapsible-list-item:last-child {
  border-bottom: 0;
}

.nested-collapsible-list ::v-deep .collapsible-list-item > .w-full {
  @apply pl-6 py-3;
  align-items: center;
}

.nested-collapsible-list ::v-deep .collapsible-list-item-content {
  border-top: 0 !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.nested-collapsible-list ::v-deep .collapsible-list-item-content.is-expanded {
  overflow-x: hidden;
}

.nested-collapsible-list ::v-deep .collapsible-list-item.closed .arrow-container-icon {
  transform: rotate(90deg) !important;
}

.nested-collapsible-list ::v-deep .collapsible-list-item.open .arrow-container-icon {
  transform: rotate(180deg) !important;
}
</style>
